import React, { useState, useEffect, useMemo } from 'react';

const faqData = [
  // General Questions
  {
    question: "What is Weartwice?",
    answer: "Weartwice is a second-hand clothing platform that connects buyers and sellers in the UAE. Weartwice is a UAE marketplace to buy and sell second-hand clothes, pre-loved fashion, shoes and accessories in Dubai and across the UAE through a secure in-app payment and delivery process. We offer a secure, easy-to-use platform where users can buy and sell pre-loved fashion items.",
    category: "general"
  },
  {
    question: "How does Weartwice work?",
    answer: "Sellers list their items for sale, and buyers can browse, purchase, and have the items collected and delivered by our shipping partner. Weartwice holds the buyer's payment until the order is confirmed, so both sides can trade with confidence. All payments, shipping arrangements and transaction-related communication must stay within the Weartwice platform. Once an order is marked as delivered, the buyer has 48 hours to validate the transaction or open a claim. If no claim is opened within that period, the transaction is automatically treated as accepted and the payment is released to the seller.",
    category: "general"
  },
  {
    question: "Is Weartwice available outside the UAE?",
    answer: "Currently, Weartwice operates within the UAE only. We may expand to other regions in the future.",
    category: "general"
  },
  {
    question: "How do I sign up?",
    answer: "You can sign up by clicking the \"Sign Up\" button on the homepage. You can register using your email address or by linking your account with a social media profile.",
    category: "general"
  },
  {
    question: "How do I delete my account?",
    answer: "To delete your account, go to the \"Profile\" section, then \"Settings\", then \"Profile Details\", then \"Delete your account\" and follow the instructions to close your account. Please note that deleting your account is permanent.",
    category: "general"
  },
  {
    question: "Can I buy or sell outside the app after speaking with another user?",
    answer: "No. All payments, delivery arrangements and transaction-related exchanges must remain on Weartwice. This is necessary for Buyer Protection, delivery tracking, payment security and dispute handling.",
    category: "general"
  },
  {
    question: "Can I swap, trade or give away items on Weartwice?",
    answer: "No. Weartwice only supports sales completed through its payment and shipping system. Swaps, trades, gifts and other non-monetary transactions are not allowed.",
    category: "general"
  },

  // For Sellers
  {
    question: "How do I sell on Weartwice?",
    answer: "Create an account. List your item by uploading clear photos and providing a detailed description, including the brand, size, condition, and price (please refer to our acceptable use policy for more details). Once a buyer purchases the item, you'll be notified and collection will be arranged with AJEX. You just need to have your item ready for the courier to collect. Each listing must be for one item only. Your photos must be original photos taken by you, and your description must accurately reflect the item's real condition, size, material, defects, alterations and signs of wear. The item must also be listed in the correct category, sub-category, size and brand field.",
    category: "sellers"
  },
  {
    question: "Are there any fees for selling?",
    answer: "There are no selling fees. Sellers keep the full item price they set. Buyers pay a Buyer Protection Fee of 20% of the item price, plus a fixed shipping fee of 20 AED per order. Full details are available in our Terms of Use. This makes Weartwice a zero-commission way to sell second-hand clothes in Dubai and across the UAE.",
    category: "sellers"
  },
  {
    question: "How do I price my items?",
    answer: "Pricing is up to you, but we recommend checking similar items on the platform to set a competitive price. Remember to consider the item's condition, brand, and demand. For better results, price your item according to its actual condition, brand, demand and how quickly you want to sell. Competitive pricing usually helps second-hand fashion items sell faster online in Dubai.",
    category: "sellers"
  },
  {
    question: "What items can I sell?",
    answer: "You can sell second-hand clothing, shoes, and accessories. Items must be clean, in good condition, and accurately described. For more details on prohibited items, refer to our Terms of Use. Items must be authentic, lawfully placed on the market, and must not infringe any third-party intellectual property rights. Counterfeit items are strictly prohibited.",
    category: "sellers"
  },
  {
    question: "How do I ship my item?",
    answer: "You don't need to pack the item yourself. AJEX collects the item and handles the packaging at collection. You just need to have the item ready, accessible and in the same condition as shown in your listing. All shipments for Weartwice orders must be carried out exclusively through AJEX using the integrated shipping flow. Sellers cannot organise delivery independently or use another courier for a platform order.",
    category: "sellers"
  },
  {
    question: "Can I cancel a sale?",
    answer: "At the moment, order cancellation is not available. Please make sure your listing is accurate and that you are ready to sell before accepting a purchase. Returns are only accepted for eligible cases, such as an item damaged during transit or significantly not as described. Change of mind, size issues (when the listing is accurate), or colour preference are not valid return reasons.",
    category: "sellers"
  },
  {
    question: "What happens if my item is damaged during shipping?",
    answer: "If an item is damaged during transit, Weartwice has insurance cover for users up to 10,000 USD, based on the sale price set on Weartwice (not the item's original retail value). We will open an investigation with AJEX and aim to close it within up to two working weeks. If the courier is found responsible based on the evidence available, AJEX compensates Weartwice and Weartwice pays the seller accordingly. A delivery delay by itself does not automatically create a right to compensation or refund.",
    category: "sellers"
  },
  {
    question: "When do I get paid?",
    answer: "The buyer's payment is held until the order is confirmed. Once the buyer confirms everything is OK, or if 48 hours pass after delivery with no claim, your funds are released to your Weartwice wallet immediately. Wallet balances are paid out automatically to the IBAN you provided once a month, on the 5th. Payouts may be delayed or temporarily suspended if Weartwice needs to investigate a claim, suspected fraud, a compliance issue or a breach of the platform rules.",
    category: "sellers"
  },
  {
    question: "What if the buyer says the item is not as described?",
    answer: "If the buyer opens a claim within 48 hours of delivery, Weartwice may review photos, messages, tracking data and any other relevant evidence. If the claim is confirmed, the buyer may be asked to return the item through the platform.",
    category: "sellers"
  },
  {
    question: "What happens if a returned item comes back in a different condition?",
    answer: "The seller should only validate the return after checking both the parcel and the item. Where relevant, Weartwice may compare available courier photos, return records and other evidence to assess the overall condition of the returned item.",
    category: "sellers"
  },
  {
    question: "Can I list several items in one listing?",
    answer: "No. Each listing must concern one item only, unless Weartwice specifically offers an approved bundle format through the platform.",
    category: "sellers"
  },
  {
    question: "Can buyers make offers on my items?",
    answer: "Yes. Buyers may make offers through the app. You can accept, reject or counter an offer. A sale is only final once the buyer completes payment through Weartwice.",
    category: "sellers"
  },
  {
    question: "How do I sell second-hand clothes online in Dubai with Weartwice?",
    answer: "Create an account, upload your item with accurate photos and details, set your price, wait for a buyer, then hand the sold item to AJEX when collection is arranged. Once the order is completed and not disputed, the sale amount is released to your Weartwice wallet.",
    category: "sellers"
  },

  // For Buyers
  {
    question: "How do I buy on Weartwice?",
    answer: "Create an account and browse the items. When you find something you like, tap \"Buy\" and follow the steps to complete the purchase. AJEX will collect from the seller and deliver to your address.",
    category: "buyers"
  },
  {
    question: "How are payments processed?",
    answer: "Payments are securely processed through our platform using our trusted partner Mamo. Your payment is held by Weartwice until you receive the item and confirm everything is OK. You have 48 hours after delivery to confirm your order or open a claim. If you do nothing within 48 hours, the order is treated as accepted and the payment is released to the seller.",
    category: "buyers"
  },
  {
    question: "How long does shipping take?",
    answer: "Delivery typically within 1 business day across the UAE. Both the buyer and the seller receive a tracking number as soon as the buyer pays, and tracking updates will appear as the courier progresses. Delivery times are estimates only and are not guaranteed. Timing may vary depending on seller availability, buyer availability, building access, remote areas, traffic, weather, public holidays, security constraints or other operational factors outside Weartwice's control.",
    category: "buyers"
  },
  {
    question: "What is Buyer Protection?",
    answer: "Buyer Protection is included with every purchase on Weartwice. It protects the buyer if an item is damaged during transport or is significantly not as described in the listing. If no issue is reported within 48 hours of delivery, the payment is released to the seller automatically. Buyer Protection is a paid platform service linked to secure payment processing, escrow, support assistance, dispute handling and, where eligible, a refund. It applies only to purchases completed through the Weartwice buy flow. It may cover eligible cases where an item is lost in transit, damaged in transit, or materially not as described, including confirmed counterfeit items.",
    category: "buyers"
  },
  {
    question: "Can I return an item?",
    answer: "Returns are accepted only in specific cases, such as an item damaged during transit, an item significantly not as described, or a confirmed counterfeit item. Returns are not accepted for change of mind, personal preference, or size issues when the listing is accurate. If a return is approved, it must be completed through the platform using AJEX Return to Shipper.",
    category: "buyers"
  },
  {
    question: "What should I do if I receive a counterfeit or incorrect item?",
    answer: "Open a claim within 48 hours of delivery. Weartwice reviews claims within 72 hours and may request supporting evidence from both sides. If the claim is approved and a return is required, the return is organised through the platform. The return shipping fee is currently AED 20 and is borne by the buyer, unless Weartwice confirms otherwise in writing or applicable law requires otherwise. If a refund is granted, it is processed once the return is delivered back to the seller and the seller validates the return in the platform, unless Weartwice requires different handling. Weartwice may permanently suspend a user account after two confirmed counterfeit cases and may report the matter to the relevant authorities where appropriate.",
    category: "buyers"
  },
  {
    question: "How do I track my order?",
    answer: "You'll receive a tracking number as soon as you pay, and both you and the seller can follow updates in the app as the courier progresses. Tracking and delivery status information is generated by AJEX and may sometimes be delayed, incomplete or updated after the fact.",
    category: "buyers"
  },
  {
    question: "What if my item is damaged during shipping?",
    answer: "Open a claim within 48 hours of delivery and share the requested evidence. If the damage is confirmed as transit-related, Weartwice will refund you and investigate the incident with AJEX. A damaged parcel does not automatically mean the item inside is damaged, so Weartwice may ask for specific photos of the packaging and the item.",
    category: "buyers"
  },
  {
    question: "Can I cancel my order?",
    answer: "At the moment, order cancellation is not available. Please double-check your order before paying. Returns are only accepted for eligible cases, such as an item damaged during transit or significantly not as described.",
    category: "buyers"
  },
  {
    question: "Can I buy multiple items in one order?",
    answer: "Yes, you can buy multiple items as a bundle. Buyer Protection applies to the total bundle value, and the fixed shipping fee stays 20 AED per order. If you open a claim, you can do so for the full bundle or only part of it, depending on what's affected. Bundles apply to multiple items from the same seller.",
    category: "buyers"
  },
  {
    question: "What happens if my order shows as delivered but I did not receive it?",
    answer: "You must open a claim within 48 hours of the order being marked as delivered in the platform. Weartwice may investigate the issue with AJEX using tracking data, proof of delivery and other available evidence.",
    category: "buyers"
  },
  {
    question: "Who pays return shipping if my claim is approved?",
    answer: "For approved Buyer Protection returns, the return shipping fee is currently AED 20 and is paid by the buyer, unless Weartwice states otherwise in writing or applicable law requires otherwise.",
    category: "buyers"
  },
  {
    question: "What refund do I receive if my claim is approved?",
    answer: "Unless Weartwice confirms otherwise in writing or applicable law requires otherwise, an approved refund includes the item price and the initial shipping fee paid for the order. The return shipping fee is borne by the buyer and is not refundable.",
    category: "buyers"
  },
  {
    question: "What if the item does not fit me?",
    answer: "A fit or sizing issue is not a valid return reason if the listing was accurate. Buyers should carefully review the listing details, photos and size information before purchasing.",
    category: "buyers"
  },
  {
    question: "How do I buy pre-loved clothes in Dubai on Weartwice?",
    answer: "Browse listings in the app, choose your item, pay securely through Weartwice, and track delivery by AJEX. Your payment stays protected until you confirm the order or the 48-hour claim window expires.",
    category: "buyers"
  },

  // Account and Payment Issues
  {
    question: "How do I update my account information?",
    answer: "You can update your account details by logging in and navigating to the \"Account Settings\" section.",
    category: "account"
  },
  {
    question: "How do I change my payment method?",
    answer: "Go to \"Payment Settings\" under your account and update your payment information. You can choose from various payment options including Visa, Mastercard, Apple Pay, Google Pay.",
    category: "account"
  },
  {
    question: "What if I forget my password?",
    answer: "Click \"Forgot Password\" on the login page, and we will send you a link to reset your password.",
    category: "account"
  },
  {
    question: "What do I do if I see unauthorised charges?",
    answer: "If you notice any unauthorised transactions, contact our support team immediately. We will investigate and help resolve the issue.",
    category: "account"
  },
  {
    question: "Why is my payout not arriving yet?",
    answer: "Monthly payouts are made automatically to the IBAN saved on your account, usually on or around the 5th calendar day of the month. If your payout is delayed, it may be because your bank details are incomplete, additional verification is required, or a claim or compliance check is still pending.",
    category: "account"
  },
  {
    question: "Do I need to add my IBAN to get paid?",
    answer: "Yes. Sellers must provide valid bank account details, including IBAN, so wallet balances can be paid out automatically.",
    category: "account"
  },

  // Shipping and Delivery
  {
    question: "Who handles shipping?",
    answer: "All shipping is handled by our trusted partner, AJEX. They ensure that your items are collected, packaged, transported, and delivered.",
    category: "shipping"
  },
  {
    question: "Who pays for shipping?",
    answer: "The buyer pays a fixed shipping fee of 20 AED per order.",
    category: "shipping"
  },
  {
    question: "What if my package doesn't arrive?",
    answer: "If your package doesn't arrive within the expected delivery time, please contact our support team. We will investigate the delay with AJEX and keep you updated on the status. If the order is marked as delivered but you have not received it, open a claim within 48 hours through the app.",
    category: "shipping"
  },
  {
    question: "What happens if the courier cannot deliver my package?",
    answer: "If delivery cannot be completed because of an incorrect address, inability to reach the buyer, repeated failed delivery attempts, or refusal to accept the parcel, the item may be returned to the seller. If the failed delivery is attributable to the buyer, Weartwice may charge the buyer the return-to-sender cost where permitted.",
    category: "shipping"
  },
  {
    question: "Can AJEX contact me by phone or WhatsApp?",
    answer: "Yes. AJEX may contact buyers by phone or WhatsApp to facilitate delivery. Depending on the address and local operating constraints, AJEX may also offer options such as leaving the parcel at the door or with building security where the buyer requests or accepts this option.",
    category: "shipping"
  },
  {
    question: "Is next-day delivery guaranteed?",
    answer: "No. Any reference to 24-hour or next-day delivery is an estimate only, not a guarantee.",
    category: "shipping"
  },

  // Privacy and Security
  {
    question: "How does Weartwice protect my data?",
    answer: "We take your privacy seriously and comply with all applicable data protection laws. For details, please refer to our Privacy Policy.",
    category: "privacy"
  },
  {
    question: "Is my payment information secure?",
    answer: "Yes, all payment information is encrypted and processed through secure payment gateways to protect your financial data.",
    category: "privacy"
  },
  {
    question: "How do I report a suspicious seller or buyer?",
    answer: "If you come across a suspicious listing or user, please report them immediately to our support team by using the \"Report\" button on the item or user profile.",
    category: "privacy"
  },
  {
    question: "What should I report immediately?",
    answer: "You should report counterfeit concerns, misleading listings, suspicious behaviour, abusive messages, off-platform payment requests, or any attempt to move delivery or payment outside Weartwice.",
    category: "privacy"
  }
];

function FAQItem({ question, answer, isOpen, onToggle }) {
  return (
    <div className="border-b border-gray-200 py-4">
      <button
        className="flex justify-between items-center w-full text-left"
        onClick={onToggle}
      >
        <h3 className="text-lg font-semibold text-[#062319] pr-8">{question}</h3>
        <span
          className="text-2xl text-[#137C63] flex-shrink-0 transition-transform duration-300"
          style={{ transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)' }}
        >
          +
        </span>
      </button>
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          maxHeight: isOpen ? '500px' : '0',
          opacity: isOpen ? 1 : 0,
          marginTop: isOpen ? '12px' : '0'
        }}
      >
        <p className="text-gray-700 text-left leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

function FAQ({ limit, showTitle = true }) {
  const [openIndex, setOpenIndex] = useState(null);

  const displayFaqs = useMemo(
    () => (limit ? faqData.slice(0, limit) : faqData),
    [limit]
  );

  useEffect(() => {
    // Inject FAQPage structured data matching what's actually shown, so it's
    // captured in the react-snap prerendered snapshot and stays in sync with
    // the visible questions/answers (Google requires the two to match).
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: displayFaqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    }).replace(/</g, '\\u003c');
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [displayFaqs]);

  return (
    <div className="bg-white py-12 md:py-16">
      <div className="container mx-auto px-4">
        {showTitle && (
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#137c63] mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about buying and selling pre-loved fashion in Dubai and UAE
            </p>
          </div>
        )}

        <div className="max-w-3xl mx-auto">
          {displayFaqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onToggle={() => {
                const isOpening = openIndex !== index;
                setOpenIndex(isOpening ? index : null);
                if (isOpening && window.gtag) {
                  window.gtag('event', 'faq_open', { faq_question: faq.question });
                }
              }}
            />
          ))}
        </div>

        {limit && (
          <div className="text-center mt-8">
            <a
              href="/faq"
              className="inline-block py-3 px-8 text-[#DDFDB3] bg-[#062319] rounded-xl hover:bg-[#093728] transition-colors font-bold"
            >
              View All FAQs
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default FAQ;
